import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { useFlags, useRequestData } from 'wsm-common-data';
import 'slick-carousel/slick/slick.scss';
import { setClassNames } from 'ddc-classnames-js';
import { trackEvent } from 'ddc-track-event';
import VehicleImage from './VehicleImage';
import CarouselButton from './CarouselButton';
import { getFlagValue } from '../../../utilities/flags';
import { getVehicleObj } from '../../../utilities/vehicleTracking';

const SPEED = 100;
const PRELOAD_RANGE = 3;

const VehicleImageCarousel = ({
	images,
	isShowVideoModalButton,
	link,
	uuid,
	videoButton
}) => {
	const { widgetName, windowId, deviceType } = useRequestData();

	const TOTAL_IMAGES = images.length || 0;
	const BTN_DISPLAY_CLASS = 'd-lg-block';
	const isMobile = deviceType === 'MOBILE';
	/**
	 * Defines a photo load range based on a range after the active photo index
	 * Returns a `new Set()` containing the range of index numbers to be loaded
	 * @param {number} active - A number defining the active photo index
	 * @param {set} loaded - A set of previously loaded photo indexes
	 */
	const getLoadRange = (range, loaded = new Set()) =>
		new Set(
			window?._?.range(...range)
				.concat(...loaded)
				.filter((index) => index >= 0 && index < TOTAL_IMAGES) || []
		);

	const slider = useRef();
	const flags = useFlags();
	const [active, setActive] = useState(0);
	const [loaded, setLoaded] = useState(getLoadRange([1]));
	const [hasImageLoaded, setHasImageLoaded] = useState(false);
	const [placeholderHeight, setPlaceholderHeight] = useState(null);
	const [prevButtonClasses, setPrevButtonClasses] = useState('');
	const [nextButtonClasses, setNextButtonClasses] =
		useState(BTN_DISPLAY_CLASS);
	const hasIndicators = images.length > 1;

	const srpFixImageCarouselPreloads = getFlagValue(
		flags,
		'srp-fix-image-carousel-preloads',
		'_toggleSRPFixImageCarouselPreloads'
	);
	// Move carousel-indicator dots over the vehicle image
	const enableRepositionCarouselIndicatorDots =
		flags['enable-reposition-carousel-indicator-dots'];
	let repositionDot = null;
	let hideButton = 'hide-buttons';
	let spacingImageAndTitle = hasIndicators ? 'indicators-after' : null;
	// update reposition for carousel-indicator dots
	if (enableRepositionCarouselIndicatorDots) {
		repositionDot = 'carousel-indicator-list--resposition';
		hideButton = null;
		spacingImageAndTitle = isShowVideoModalButton ? 'mb-0' : 'mb-3';
	}

	useEffect(() => {
		if (hasImageLoaded) {
			// `resize` event required to trigger slider observer and adapt height
			const event = new Event('resize');
			window.dispatchEvent(event);

			// hack for iOS bug to initialize first 2 slides of visible carousel
			if (isMobile) {
				slider.current.slickGoTo(1, true);
				slider.current.slickGoTo(0, true);
			}
		}
	}, [hasImageLoaded, isMobile]);

	const trackInteraction = (swipe) => {
		let element;
		let result;
		if (swipe === 'left') {
			element = 'Photo carousel next button';
			result = 'Move to next photo';
		} else if (swipe === 'right') {
			element = 'Photo carousel previous button';
			result = 'Move to previous photo';
		}
		trackEvent(widgetName, windowId, {
			element,
			result,
			uuid,
			vehicle: getVehicleObj(uuid)
		});
	};
	const onNext = () => {
		slider.current.slickNext();
		trackInteraction('left');
	};
	const onPrev = () => {
		slider.current.slickPrev();
		trackInteraction('right');
	};

	const afterChange = (index) => {
		setActive(index);
		setPrevButtonClasses(index !== 0 ? BTN_DISPLAY_CLASS : '');
		setNextButtonClasses(
			index !== TOTAL_IMAGES - 1 ? BTN_DISPLAY_CLASS : ''
		);
	};

	const loadRange = (next, range) => {
		setLoaded(getLoadRange([next, next + range], loaded));
	};

	if (images && TOTAL_IMAGES) {
		return (
			<Slider
				ref={slider}
				className={setClassNames([
					'carousel',
					'slick-loading',
					hideButton,
					spacingImageAndTitle
				])}
				dots={enableRepositionCarouselIndicatorDots ? isMobile : true}
				adaptiveHeight
				infinite={false}
				speed={SPEED}
				slidesToShow={1}
				slidesToScroll={1}
				prevArrow={
					<CarouselButton
						direction="left"
						clickEvent={onPrev}
						mouseUpEvent={(e) => e.target.blur()}
						classes={['previous', prevButtonClasses]}
					/>
				}
				nextArrow={
					<CarouselButton
						direction="right"
						clickEvent={onNext}
						mouseUpEvent={(e) => e.target.blur()}
						classes={['next', nextButtonClasses]}
					/>
				}
				onInit={() => {
					if (!srpFixImageCarouselPreloads) {
						return;
					}
					loadRange(1, 1);
				}}
				beforeChange={(current, next) => {
					if (srpFixImageCarouselPreloads) {
						loadRange(next, PRELOAD_RANGE);
					} else if (!loaded.has(next)) {
						const range = isMobile && current === 0 ? 1 : 3;
						setLoaded(getLoadRange([next, next + range], loaded));
					}
					if (isMobile) {
						// iOS issue with swapping back to slide before change occurred
						// need to wait to change state until change has completed
						setTimeout(() => {
							afterChange(next);
						}, SPEED);
					} else {
						afterChange(next);
					}
				}}
				onSwipe={(swipe) => trackInteraction(swipe)}
				dotsClass="carousel-indicators-container"
				appendDots={(dots) => (
					<div role="presentation" aria-hidden="true">
						<ul
							data-testid="carousel-indicator-list"
							className={`carousel-indicator-list dots infinite-dots ${repositionDot}`}
						>
							{enableRepositionCarouselIndicatorDots ? (
								<div
									data-testid="carousel-indicator-wrapper"
									className="carousel-indicator-wrapper"
								>
									{dots.map((dot, index) => (
										<li
											key={dot.key}
											className={setClassNames([
												active <= index + 3 &&
												active >= index - 3
													? null
													: 'd-none'
											])}
										>
											{dot.props.children}
										</li>
									))}
								</div>
							) : (
								dots.map((dot, index) => (
									<li
										key={dot.key}
										className={setClassNames([
											active <= index + 3 &&
											active >= index - 3
												? null
												: 'd-none'
										])}
									>
										{dot.props.children}
									</li>
								))
							)}
						</ul>
						{enableRepositionCarouselIndicatorDots
							? null
							: videoButton}
					</div>
				)}
				customPaging={(index) => (
					<span
						className={setClassNames([
							'carousel-indicator',
							'dot',
							active === index ? 'active' : null,
							active === index - 2 || active === index + 2
								? 'dot-sm'
								: null,
							active === index - 3 || active === index + 3
								? 'dot-xs'
								: null
						])}
					/>
				)}
			>
				{images.map((image, index) => (
					<VehicleImage
						key={image.id}
						enableImage={loaded.has(index)}
						imageIndex={index}
						onImageLoad={(imageRef) => {
							setHasImageLoaded(true);
							setPlaceholderHeight(imageRef.current.height);

							// Only do this on the first image because this code applies to the
							// entire slider and we only want to run it once per Slick Slider.
							if (index > 0) {
								return;
							}

							// Get the parent container
							const imageContainer = imageRef.current.closest(
								'.vehicle-card-media-container'
							);

							// Get the placeholder vehicle image
							const placeholder = imageContainer.querySelector(
								'.vehicle-image-skeleton'
							);

							// Stop early if we can't find the placeholder
							if (!placeholder) {
								return;
							}

							// Get the Slick Slider
							const slickSlider =
								imageContainer.querySelector('.slick-slider');

							// Stop early if we can't find the Slick Slider
							if (!slickSlider) {
								return;
							}

							// This timeout is arbitrary but gives Slick enough
							// time to make the slider before displaying it.
							setTimeout(() => {
								// Hide the placeholder
								if (placeholder) {
									placeholder.classList.add('d-none');
								}

								// And show the Slick Slider
								if (slickSlider) {
									slickSlider.classList.remove(
										'slick-loading'
									);
								}

								// Remove role presentation of image
								imageRef.current.removeAttribute('role');
							}, 400);
						}}
						placeholderHeight={
							index === 1 ? placeholderHeight : null
						}
						{...{
							image,
							link
						}}
						uuid={uuid}
					/>
				))}
			</Slider>
		);
	}

	return null;
};

VehicleImageCarousel.propTypes = {
	images: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			uri: PropTypes.string,
			alt: PropTypes.string,
			title: PropTypes.string
		})
	).isRequired,
	isShowVideoModalButton: PropTypes.bool,
	link: PropTypes.string,
	uuid: PropTypes.string.isRequired,
	videoButton: PropTypes.element
};

VehicleImageCarousel.defaultProps = {
	link: '',
	videoButton: null
};

export default VehicleImageCarousel;
